import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Loading, PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import useComponentState from '../../utils/useComponentState';
import styles from './ViewProducts.module.scss';
import { getAllProducts } from '../../api/Products';
import EditProductModal from '../../components/Modals/EditProductModal';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import { updateProduct, deleteProduct } from '../../api/Products'; // Make sure this import exists
import { Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Search from '../../components/Search/Search.component';

// Main component for viewing products
const ViewProducts = (props) => {
    // Local state for managing table, loading, pagination, etc.
    const [state, setState] = useComponentState({
        tableHeading: {
            column: [
                'ID',
                'Category',
                'Product Title',
                'Themes',
                'Featured',
                'Published',
                'Slug',
            ],
        },
        loading: false,
        notFoundMessage: '',
        fetchError: '',
        products: [],
        featuredFilter: false,
        publishedFilter: true,
        expanded: {},
        currentPage: 0,
        totalPages: 0,
        showEditModal: false,
        selectedProduct: null,
        showDeleteModal: false,
        productToDelete: null,
        searchTitle: '',
        searchInputValue: '',
    });

    // Destructure state for easier access
    const {
        tableHeading,
        loading,
        notFoundMessage,
        fetchError,
        products,
        currentPage,
        totalPages,
        expanded,
    } = state;

    // Fetch products when component mounts
    // Re-fetch products when filters or pagination change
    useEffect(() => {
        getProducts(state.currentPage);
        // eslint-disable-next-line
    }, [state.featuredFilter, state.publishedFilter, state.searchTitle, state.currentPage]);

    const handleFilterChange = (filterType, value) => {
        if (filterType === 'featured') {
            setState({ featuredFilter: value, currentPage: 0 });
        }
        if (filterType === 'published') {
            setState({ publishedFilter: value, currentPage: 0 });
        }
    };

    // Handle search by title - only called when user clicks search icon
    const handleSearchButtonClick = (searchValue) => {
        setState({ searchTitle: searchValue, currentPage: 0 });
    };

    // Clear search
    const handleClearSearch = () => {
        setState({ searchTitle: '', searchInputValue: '', currentPage: 0 });
    };

    // Handle input change - update local state without triggering API call
    const handleSearchInputChange = (value) => {
        setState({ searchInputValue: value });
    };

    // Fetch all products from API with server-side pagination
    const getProducts = async (page) => {
        const token = props.auth.auth.token;
        setState({ loading: true, fetchError: '', notFoundMessage: '' });
        try {
            if (!token) {
                setState({ fetchError: 'No authentication token found please login again', loading: false });
                return;
            }

            const res = await getAllProducts(
                token,
                state.featuredFilter,
                state.publishedFilter,
                page,
                20, // limit per page
                false,
                state.searchTitle
            ); // API call

            // Extract pagination info from response root level
            const pageCount = res?.pageCount || 1;
            const currentPageNum = res?.page !== undefined ? res?.page : page;

            // Extract categories and flatten products into a single array
            const categories = res?.categories || [];
            const products = categories.flatMap(cat =>
                (cat.products || []).map(prod => ({
                    ...prod,
                    category: cat.title, // Attach category title to each product
                    productCategoryId: cat.category_id
                }))
            );

            setState({
                products,
                totalPages: pageCount,
                currentPage: currentPageNum,
                loading: false,
                notFoundMessage: products.length ? '' : 'No products found.'
            });
        } catch (error) {
            setState({ fetchError: 'Failed to fetch products.', loading: false });
        }
    };

    // Handle pagination page change
    const handlePageChange = (selectedPage) => {
        getProducts(selectedPage);
    };

    // Render expandable/collapsible text for long descriptions
    const renderExpandableText = (text, productId, field, length = 80) => {
        if (!text) return '';
        const key = `${productId}_${field}`;
        const isExpanded = expanded[key];

        if (isExpanded) {
            // Show full text with "Read Less" link
            return (
                <p>
                    {text}{' '}
                    <button
                        type="button"
                        onClick={e => {
                            e.preventDefault();
                            setState({
                                expanded: {
                                    ...expanded,
                                    [key]: false,
                                }
                            });
                        }}
                        className={styles.readMoreLessButton}
                    >
                        ...Read Less
                    </button>
                </p>
            );
        }

        // Show truncated text with "Read More" link if too long
        return text.length > length ? (
            <p title={text}>
                {text.substring(0, length)}...
                {' '}
                <button
                    type="button"
                    onClick={e => {
                        e.preventDefault();
                        setState({
                            expanded: {
                                ...expanded,
                                [key]: true,
                            }
                        });
                    }}
                    className={styles.readMoreLessButton}
                >
                    Read More
                </button>
            </p>
        ) : <p>{text}</p>;
    };


    const handleEditProduct = (product) => {
        // This is a regular edit action
        setState({
            showEditModal: true,
            selectedProduct: product
        });
    };

    // Open confirmation modal for deleting a product
    const handleDeleteProduct = (product) => {
        setState({ showDeleteModal: true, productToDelete: product });
    };

    // Called when user confirms deletion in the modal
    const performDeleteProduct = async () => {
        const product = state.productToDelete;
        if (!product) return;
        try {
            setState({ loading: true });
            await deleteProduct(props.auth.auth.token, product.id);
            toast.success('Product deleted successfully.');
        } catch (error) {
            toast.error(error?.message || 'Failed to delete product.');
            setState({ fetchError: 'Failed to delete product.' });
        } finally {
            // Refresh list and hide modal
            try {
                await getProducts(state.currentPage);
            } catch (err) {
                setState({ fetchError: 'Failed to refresh products.' });
            }
            setState({ loading: false, showDeleteModal: false, productToDelete: null });
        }
    };

    const handleDeleteCancel = () => {
        setState({ showDeleteModal: false, productToDelete: null });
    };

    const handleUpdateProductFeatured = async (product) => {
        try {
            setState({ loading: true });
            await updateProduct(props.auth.auth.token, product.id, { featured: product.featured });
            await getProducts(state.currentPage);
        } catch (error) {
            setState({
                fetchError: 'Failed to update product status.',
                loading: false
            });
        }
    };

    const handleUpdateProductPublished = async (product) => {
        try {
            setState({ loading: true });
            await updateProduct(props.auth.auth.token, product.id, { published: product.published });
            await getProducts(state.currentPage);
        } catch (error) {
            setState({
                fetchError: 'Failed to update product status.',
                loading: false
            });
        }
    };

    const handleEditModalClose = () => {
        setState({
            showEditModal: false,
            selectedProduct: null
        });
    };

    const handleEditSuccess = () => {
        getProducts(state.currentPage);
        handleEditModalClose();
    };



    return (
        <div className={styles.root_div}>
            {/* Page header */}
            <PageHeader text="View Products" />
            <br />


            <div className={styles.filterRow}>
                <div>
                    <Dropdown>
                        <Dropdown.Toggle variant='primary' id='dropdown-basic'>
                            {state.featuredFilter === false ? 'Non-Featured' : 'Featured'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {/* Add filter options here */}
                            {
                                state.featuredFilter === true ? (
                                    <Dropdown.Item onClick={() => handleFilterChange('featured', false)}>Non-Featured Products</Dropdown.Item>
                                ) : (
                                    <Dropdown.Item onClick={() => handleFilterChange('featured', true)}>Featured Products</Dropdown.Item>
                                )
                            }
                        </Dropdown.Menu>

                    </Dropdown>
                </div>

                <div>
                    <Dropdown>
                        <Dropdown.Toggle variant='primary' id='dropdown-basic'>
                            {state.publishedFilter === false ? 'Not Published' : 'Published'}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {
                                state.publishedFilter === true ? (
                                    <Dropdown.Item onClick={() => handleFilterChange('published', false)}>Not Published Products</Dropdown.Item>
                                ) : (
                                    <Dropdown.Item onClick={() => handleFilterChange('published', true)}>Published Products</Dropdown.Item>
                                )
                            }

                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                {/* Search by title */}
                <Search
                    placeholder="Search by product title..."
                    onBtnPress={handleSearchButtonClick}
                    onClearPress={handleClearSearch}
                    value={state.searchInputValue}
                    onChange={handleSearchInputChange}
                    handleValueFromParent={true}
                />

            </div>



            {/* Show loading spinner or data table */}
            <div className={styles.voucherTableWrapper}>
                <DataTable
                    tableData={products}
                    tableheading={tableHeading}
                    tag="products"
                    fetchError={fetchError}
                    expanded={expanded}
                    notFoundMessage={notFoundMessage}
                    search_filter_loading={loading}
                    loading={loading}
                    handleEditProduct={handleEditProduct}
                    handleDeleteProduct={handleDeleteProduct}
                    handleUpdateProductFeatured={handleUpdateProductFeatured}
                    handleUpdateProductPublished={handleUpdateProductPublished}
                    renderExpandableText={(text, productId, field) =>
                        renderExpandableText(text, productId, field)
                    }
                >
                    {/* Insert Loading inside the DataTable */}
                    <Loading />
                </DataTable>
            </div>
            {/* Pagination controls */}
            <Pagination
                pageCount={totalPages}
                currentPage={currentPage}
                onChange={handlePageChange}
            />

            {/* Edit Product Modal */}
            <EditProductModal
                show={state.showEditModal}
                onHide={handleEditModalClose}
                product={state.selectedProduct}
                onSuccess={handleEditSuccess}
            />

            {/* Delete confirmation modal */}
            <CancelAlertPopup
                show={state.showDeleteModal}
                alertMessage={`Are you sure you want to delete product: ${state.productToDelete?.title || state.productToDelete?.id}?`}
                handleYesBtnClick={performDeleteProduct}
                handleNoBtnClick={handleDeleteCancel}
            />
        </div >
    );
};

// Map Redux state to component props
const mapStateToProps = (state) => {
    return {
        auth: state.auth,
    };
};


// Connect component to Redux store
export default connect(mapStateToProps)(ViewProducts);

